import { useQuery } from '@tanstack/react-query';
import { CalendarClock, FileText, Fuel, Info, Receipt } from 'lucide-react';

import { CardIndicador } from '@/componentes/CardIndicador';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { SemPermissao } from '@/componentes/SemPermissao';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarCompetencia, formatarDia, formatarReais } from '@/lib/formato';
import { perfilAPedir } from '@/lib/perfil-da-licenca';

import { apiDoFinanceiro, type FaturaAPagar, type MedicaoAFaturar, type TituloAReceber, type TituloDaMatriz } from './api';
import { reaisComSinal, SITUACAO_DA_FATURA, SITUACAO_DO_TITULO } from './financeiro';

/** Dias inteiros de `de` até hoje, no relógio de quem olha. */
function diasDesde(de: string): number {
  const [a, m, d] = de.split('-').map(Number) as [number, number, number];
  const hoje = new Date();
  return Math.max(0, Math.round((Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()) - Date.UTC(a, m - 1, d)) / 86_400_000));
}

/**
 * FERRAMENTA 1 DO MÓDULO 15 — os títulos que a operação já conhece, lidos da
 * origem: o documento de faturamento da medição aprovada (módulo 14), a
 * medição aprovada ainda sem documento, e a fatura do posto (módulo 08).
 *
 * O resto do v6 ("vencimentos, baixas") é do ERP, sistema mestre fiscal (guia
 * 16.3), e entra pela matriz do módulo 04 (0108): os títulos da matriz, com o
 * vencimento e a baixa, aparecem abaixo, e a cobrança que tem título ligado
 * conta uma vez só, pelo título. Aqui não se registra baixa nenhuma: ela vem
 * do mestre, ou é lançada em Cadastros › Integrações › Títulos e notas.
 */
export function TitulosDaOperacao() {
  const titulos = useQuery({ queryKey: ['financeiro', 'titulos'], queryFn: apiDoFinanceiro.titulos, meta: { oQueLia: 'os títulos' } });
  const t = titulos.data;
  const semReceita = t?.semLicenca.includes('receita.ler') ?? false;
  const semCombustivel = t?.semLicenca.includes('combustivel.ler') ?? false;
  const erro = titulos.isError ? <ErroDaLista oQue="Os títulos" erro={titulos.error} aoTentarDeNovo={() => void titulos.refetch()} /> : null;

  return (
    <>
      <FaixaIndicadores>
        <CardIndicador
          rotulo="A receber em aberto"
          icone={Receipt}
          tom="disponivel"
          carregando={!t}
          valor={t ? formatarReais(t.totais.aReceberEmAberto) : ''}
          contexto="Documentos emitidos sem o recebimento inteiro"
        />
        <CardIndicador
          rotulo="A faturar"
          icone={FileText}
          tom="standby"
          carregando={!t}
          valor={t ? formatarReais(t.totais.aFaturar) : ''}
          alerta={(t?.totais.aFaturar ?? 0) > 0}
          contexto="Medições aprovadas sem documento emitido"
        />
        <CardIndicador
          rotulo="Faturas de posto em aberto"
          icone={Fuel}
          tom="neutro"
          carregando={!t}
          valor={t ? formatarReais(t.totais.aPagarEmAberto) : ''}
          contexto="Cobrança dos postos ainda não conciliada"
        />
        <CardIndicador
          rotulo="Títulos a pagar"
          icone={CalendarClock}
          tom="standby"
          carregando={!t}
          valor={t ? formatarReais(t.totais.titulosAPagarEmAberto ?? 0) : ''}
          contexto="Em aberto na matriz de integração, com vencimento"
        />
      </FaixaIndicadores>

      <div className="flex items-start gap-3">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-disponivel" />
        <p className="txt-apoio text-pretty text-tinta-secundaria">{t?.frase ?? 'Os títulos que a operação já conhece, lidos dos módulos de origem.'}</p>
      </div>

      {semReceita ? (
        <SemPermissao modulo="os documentos de faturamento e as medições" perfil={perfilAPedir('receita.ler')} titulo="Você não vê os títulos a receber" />
      ) : (
        <>
          <h2 className="txt-secao text-tinta">A receber</h2>
          <Tabela
            rotulo="A receber: documentos de faturamento"
            colunas={COLUNAS_A_RECEBER}
            linhas={t?.aReceber ?? []}
            chaveDaLinha={(d) => d.id}
            carregando={titulos.isPending}
            alturaDupla
            erro={erro}
            vazio={<EstadoVazio titulo="Nenhum documento de faturamento emitido" descricao="O documento é emitido sobre a medição aprovada, em Medições e receita. Ele aparece aqui até o recebimento inteiro." />}
            rodape={<RodapeDeContagem total={t?.aReceber.length ?? 0} />}
          />
          <h2 className="txt-secao text-tinta">A faturar</h2>
          <Tabela
            rotulo="A faturar: medições aprovadas sem documento"
            colunas={COLUNAS_A_FATURAR}
            linhas={t?.aFaturar ?? []}
            chaveDaLinha={(m) => m.medicaoId}
            carregando={titulos.isPending}
            alturaDupla
            erro={erro}
            vazio={<EstadoVazio titulo="Nenhuma medição aprovada à espera de documento" descricao="Toda medição aprovada já tem documento emitido." />}
            rodape={<RodapeDeContagem total={t?.aFaturar.length ?? 0} />}
          />
        </>
      )}

      {t?.titulosDaMatriz ? (
        <>
          <h2 className="txt-secao text-tinta">Títulos da matriz de integração</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">{t.titulosDaMatriz.frase}</p>
          <Tabela
            rotulo="Títulos a pagar da matriz"
            colunas={COLUNAS_DA_MATRIZ}
            linhas={t.titulosDaMatriz.aPagar}
            chaveDaLinha={(x) => x.id}
            carregando={titulos.isPending}
            alturaDupla
            erro={erro}
            vazio={<EstadoVazio titulo="Nenhum título a pagar na matriz" descricao="Eles chegam do mestre pela importação, ou são lançados em Cadastros › Integrações › Títulos e notas." />}
            rodape={<RodapeDeContagem total={t.titulosDaMatriz.aPagar.length} />}
          />
          {semReceita ? null : (
            <Tabela
              rotulo="Títulos a receber da matriz"
              colunas={COLUNAS_DA_MATRIZ}
              linhas={t.titulosDaMatriz.aReceber}
              chaveDaLinha={(x) => x.id}
              carregando={titulos.isPending}
              alturaDupla
              erro={erro}
              vazio={<EstadoVazio titulo="Nenhum título a receber na matriz" descricao="Ligado à cobrança da medição, o título a receber passa a contar no lugar dela." />}
              rodape={<RodapeDeContagem total={t.titulosDaMatriz.aReceber.length} />}
            />
          )}
        </>
      ) : null}

      {semCombustivel ? (
        <SemPermissao modulo="a cobrança dos postos" perfil={perfilAPedir('combustivel.ler')} titulo="Você não vê as faturas dos postos" />
      ) : (
        <>
        <h2 className="txt-secao text-tinta">A pagar: faturas dos postos</h2>
        <Tabela
          rotulo="A pagar: faturas dos postos"
          colunas={COLUNAS_A_PAGAR}
          linhas={t?.aPagar ?? []}
          chaveDaLinha={(c) => c.id}
          carregando={titulos.isPending}
          alturaDupla
          erro={erro}
          vazio={<EstadoVazio titulo="Nenhuma fatura de posto registrada" descricao="A fatura do posto é registrada em Combustível, na aba Cobranças dos postos, e conciliada com os abastecimentos." />}
          rodape={<RodapeDeContagem total={t?.aPagar.length ?? 0} />}
        />
        </>
      )}
    </>
  );
}

const reais = (v: number) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(v)}</span>;

const COLUNAS_A_RECEBER: readonly Coluna<TituloAReceber>[] = [
  {
    chave: 'documento',
    rotulo: 'Documento',
    prioridade: 'chave',
    celula: (d) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{d.numero}</span>
        <TextoDaCelula texto={`emitido em ${formatarDia(d.emitidoEm)}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  {
    chave: 'contrato',
    rotulo: 'Contrato',
    celula: (d) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{d.contrato}</span>
        <TextoDaCelula texto={`${d.cliente} · competência ${formatarCompetencia(d.competencia)}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  { chave: 'valor', rotulo: 'Valor da medição', alinhamento: 'numero', celula: (d) => reais(d.valor), total: (l) => reais(l.reduce((s, d) => s + d.valor, 0)) },
  {
    chave: 'aberto',
    rotulo: 'Em aberto',
    alinhamento: 'numero',
    celula: (d) => (
      <span className="flex flex-col items-end">
        {reais(d.emAberto)}
        {d.recebidoEm === null ? <span className="txt-apoio whitespace-nowrap text-tinta-apagada">{`${String(diasDesde(d.emitidoEm))} dias desde a emissão`}</span> : null}
      </span>
    ),
    total: (l) => reais(l.reduce((s, d) => s + d.emAberto, 0)),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    celula: (d) => (
      <span className="flex min-w-0 flex-col">
        <span className={`txt-corpo ${d.situacao === 'recebido_a_menor' ? 'text-atencao-texto' : 'text-tinta'}`}>{SITUACAO_DO_TITULO[d.situacao]}</span>
        {d.recebidoEm ? <TextoDaCelula texto={`${formatarReais(d.valorRecebido ?? 0)} em ${formatarDia(d.recebidoEm)}`} classe="txt-apoio text-tinta-apagada" linhas={1} /> : null}
        {d.noTitulo ? <TextoDaCelula texto="conta pelo título da matriz" classe="txt-apoio text-tinta-apagada" linhas={1} /> : null}
      </span>
    ),
  },
];

const ORIGEM: Readonly<Record<TituloDaMatriz['origem'], string>> = { lote: 'do mestre', contingencia: 'em contingência', plataforma: 'da plataforma' };

const COLUNAS_DA_MATRIZ: readonly Coluna<TituloDaMatriz>[] = [
  {
    chave: 'titulo',
    rotulo: 'Título',
    prioridade: 'chave',
    celula: (x) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{x.numero}</span>
        <TextoDaCelula texto={`${x.contraparte} · ${ORIGEM[x.origem]}${x.sistema ? ` (${x.sistema})` : ''}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  {
    chave: 'ligacao',
    rotulo: 'Ligado a',
    prioridade: 'secundaria',
    celula: (x) => <span className="txt-corpo text-tinta">{x.pedido ? `Pedido ${x.pedido}` : x.documento ? `Cobrança ${x.documento}` : '—'}</span>,
  },
  { chave: 'vencimento', rotulo: 'Vencimento', celula: (x) => <span className="txt-corpo text-tinta">{formatarDia(x.vencimento)}</span> },
  { chave: 'valor', rotulo: 'Valor', alinhamento: 'numero', celula: (x) => reais(x.valor), total: (l) => reais(l.reduce((s, x) => s + x.valor, 0)) },
  { chave: 'aberto', rotulo: 'Em aberto', alinhamento: 'numero', celula: (x) => reais(x.emAberto), total: (l) => reais(l.reduce((s, x) => s + x.emAberto, 0)) },
];

const COLUNAS_A_FATURAR: readonly Coluna<MedicaoAFaturar>[] = [
  {
    chave: 'contrato',
    rotulo: 'Contrato',
    prioridade: 'chave',
    celula: (m) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{m.contrato}</span>
        <TextoDaCelula texto={m.cliente} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  { chave: 'competencia', rotulo: 'Competência', celula: (m) => <span className="codigo text-tinta">{formatarCompetencia(m.competencia)}</span> },
  {
    chave: 'aprovada',
    rotulo: 'Aprovada em',
    celula: (m) => <span className="txt-corpo text-tinta">{m.aprovadaEm ? formatarDia(m.aprovadaEm.slice(0, 10)) : '—'}</span>,
  },
  { chave: 'valor', rotulo: 'Valor da medição', alinhamento: 'numero', celula: (m) => reais(m.valor), total: (l) => reais(l.reduce((s, m) => s + m.valor, 0)) },
];

const COLUNAS_A_PAGAR: readonly Coluna<FaturaAPagar>[] = [
  {
    chave: 'documento',
    rotulo: 'Fatura',
    prioridade: 'chave',
    celula: (c) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{c.documento}</span>
        <TextoDaCelula texto={c.emitidaEm ? `emitida em ${formatarDia(c.emitidaEm)}` : 'sem data de emissão'} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  {
    chave: 'fornecedor',
    rotulo: 'Fornecedor',
    celula: (c) => (
      <span className="flex min-w-0 flex-col">
        <span className="txt-corpo text-tinta">{c.fornecedor}</span>
        <TextoDaCelula texto={c.posto ?? 'sem posto'} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </span>
    ),
  },
  { chave: 'competencia', rotulo: 'Competência', prioridade: 'secundaria', celula: (c) => <span className="codigo text-tinta">{c.competencia ? formatarCompetencia(c.competencia) : '—'}</span> },
  { chave: 'valor', rotulo: 'Valor cobrado', alinhamento: 'numero', celula: (c) => reais(c.valor) },
  {
    chave: 'diferenca',
    rotulo: 'Diferença',
    alinhamento: 'numero',
    celula: (c) => <span className={`numero whitespace-nowrap ${Math.abs(c.diferenca) >= 0.005 ? 'text-atencao-texto' : 'text-tinta'}`}>{reaisComSinal(c.diferenca)}</span>,
  },
  { chave: 'situacao', rotulo: 'Situação', celula: (c) => <span className="txt-corpo text-tinta">{SITUACAO_DA_FATURA[c.situacao]}</span> },
];
